/**
 * ProtocolRecipientsEditor — ADR 116 / ADR 117
 *
 * Role × subscription editor for the Protocols drawer: an explicit
 * per-role (Real Alert / Drill) grid backed by school-tier rubric cells
 * (`SchoolAlertRubricCell`).
 *
 * Each visible row corresponds to one rubric cell keyed by
 * `(school, role, alert_type)` — the **type-override** shape (the
 * `(urgency, mode)` default cells live at the district tier and are
 * managed by a separate admin surface). Two dropdowns per row encode
 * `alert_min_level` and `drill_min_level` as either NULL
 * ("Unsubscribed") or one of the v1 visible levels {0, 1, 2}.
 *
 * Real Alert and Drill are independent — each is its own subscription
 * with its own level. Changing one does NOT mutate the other; the data
 * model has independent `alert_min_level` and `drill_min_level` int
 * columns.
 *
 * Each subscription is a single dropdown with values
 * `[Unsubscribed, L0, L1, L2]` rather than a checkbox + level picker:
 * "subscribed?" and "at what level?" are one logical value, and one
 * widget with `Unsubscribed` as a first-class option is less cognitive
 * load.
 *
 * Why type-override (not default) cells from this surface
 * ======================================================
 * SMS authors edit a specific Protocol. The natural unit is "for THIS
 * protocol, which roles get notified" — which is exactly the
 * type-override shape. Default `(urgency, mode)` cells are inherited
 * from the district tier; surfacing them here would require disclosing
 * cross-protocol propagation, which is admin-surface territory.
 *
 * Append-only writes
 * ==================
 * Every dropdown change POSTs (or PATCHes if a row exists) — the
 * server's append-only history layer turns same-key writes into a
 * close-old + insert-new cutover. Local component state caches the
 * latest cell id per `(role)` key so re-renders track the rotating
 * id without forcing a refetch round-trip.
 */
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import {
  Box,
  Spinner,
  Text,
  Center,
  Select,
  Portal,
  createListCollection,
} from '@chakra-ui/react';
import { DataTable, type Column } from '@themes/aegix';
// No `RubricCellPreviewBadge` here: the protocol drawer always passes
// school scope, and district-tier previews belong to the Default
// Recipients tab.
import { useGroups } from '../../../hooks/queries';
import {
  useSchoolRubricCells,
  useCreateSchoolRubricCell,
  usePatchSchoolRubricCell,
  useDeleteSchoolRubricCell,
  useDistrictRubricCells,
  useCreateDistrictRubricCell,
  usePatchDistrictRubricCell,
  useDeleteDistrictRubricCell,
} from '../../../hooks/queries/useRubricCells';
import type {
  RubricCell,
  RubricCellWritePayload,
  RubricUrgency,
  RubricMode,
} from '../../../services';
import { useScopedFeature } from '../../../hooks/useFeature';
import { FeatureCode } from '../../../types/features';
import { toaster } from '../../ui/toaster';

/**
 * Which tier this instance edits. The two tiers share the
 * cell shape `(role, urgency=null, mode=null, alert_type=set,
 * alert_min_level, drill_min_level)` — only the scope FK differs. Same
 * grid renders for both; the discriminant just swaps which hooks the
 * editor binds to.
 *
 * School-scope can optionally include the school's
 * `districtId` so the editor can peek at the parent T1 cells and
 * render "inherited from district template" hints when a row has no
 * T2 override but T1 has a value. Optional because not every school
 * belongs to a district.
 */
export type RubricScope =
  | { tier: 'school'; schoolId: number; districtId?: number | null }
  | { tier: 'district'; districtId: number };

interface ProtocolRecipientsEditorProps {
  /** Scope this editor reads/writes. `null` when the protocol hasn't
   *  been saved yet (create flow) — editor renders the "save first"
   *  hint and no-ops writes. */
  scope: RubricScope | null;
  /** The AlertType (protocol) id. Required to scope rubric rows to
   *  the right `alert_type`. Null during create — surface a hint and
   *  no-op writes. */
  alertTypeId: number | null;
  /**
   * The protocol's urgency + activation_mode. Needed to resolve the
   * inherited district `(urgency, mode)` cell-default — the value the backend
   * seeds at protocol creation — so a role with no type-override cell renders
   * "Default · <inherited>" instead of a misleading "Unsubscribed". `follow`
   * urgency has no rubric default (operational / group-targeted), so the
   * default lookup is skipped for it.
   */
  urgency?: 'emergency' | 'attention' | 'follow' | null;
  mode?: 'alert' | 'incident' | null;
}

/** Subset of escalation levels visible to v1 SMS authors (ADR 117 §"v1
 *  scope"). The wire format is `int` and the backend accepts {0, 1, 2}. */
const LEVELS = [0, 1, 2] as const;
type Level = (typeof LEVELS)[number];

// Per-(role, axis) dropdown. A type-override cell
// carries explicit levels; the ABSENCE of a cell means the role inherits
// the district default — rendered as "Default · <inherited>",
// NOT a misleading bare "Unsubscribed". States:
//   Default · <inherited>   — no type-override; follows the rubric default
//   Unsubscribed            — explicit per-protocol opt-out (null on the wire)
//   Alert Start / 1st / 2nd — explicit per-protocol level (0/1/2)
// Picking Default reverts the row (writeCell clears → DELETE).
const DEFAULT = 'default';
const UNSUBSCRIBED = 'unsubscribed';

const LEVEL_LABELS: Record<string, string> = {
  '0': 'Alert Start',
  '1': '1st Escalation',
  '2': '2nd Escalation',
};

/**
 * With ADR-117 Mechanism B (`alert_escalation`) off, levels above 0
 * do not exist as far as this grid is concerned — an already-stored 1/2
 * (explicit cell OR inherited district default) displays as level 0.
 * Presentation only; nothing here writes, so the stored level survives and
 * re-enabling the flag brings it straight back.
 */
function displayLevel(level: number, escalationEnabled: boolean): number {
  return escalationEnabled || level <= 0 ? level : 0;
}

export function levelLabelText(
  level: number | null,
  escalationEnabled: boolean,
  t?: (key: string, fallback: string) => string,
): string {
  if (level == null)
    return t ? t('protocols.unsubscribed', 'Unsubscribed') : 'Unsubscribed';
  const shown = displayLevel(level, escalationEnabled);
  if (t) {
    switch (String(shown)) {
      case '0':
        return t('protocols.alertStart', 'Alert Start');
      case '1':
        return t('protocols.firstEscalation', '1st Escalation');
      case '2':
        return t('protocols.secondEscalation', '2nd Escalation');
    }
  }
  return LEVEL_LABELS[String(shown)] ?? `Level ${shown}`;
}

/** Per-axis option list. The leading "Default · <inherited>" reflects what
 *  the role resolves to when no type-override exists for this protocol.
 *  The 1st/2nd Escalation options only appear when ADR-117 Mechanism B is
 *  enabled for the org. */
export function subscriptionCollectionFor(
  inheritedLevel: number | null,
  escalationEnabled: boolean,
  t?: (
    key: string,
    fallback: string,
    options?: Record<string, unknown>,
  ) => string,
) {
  const inheritedLabel = levelLabelText(
    inheritedLevel,
    escalationEnabled,
    t as (key: string, fallback: string) => string,
  );
  return createListCollection({
    items: [
      {
        label: t
          ? t('protocols.defaultInherited', `Default · ${inheritedLabel}`, {
              label: inheritedLabel,
            })
          : `Default · ${inheritedLabel}`,
        value: DEFAULT,
      },
      {
        label: t ? t('protocols.unsubscribed', 'Unsubscribed') : 'Unsubscribed',
        value: UNSUBSCRIBED,
      },
      {
        label: t ? t('protocols.alertStart', 'Alert Start') : 'Alert Start',
        value: '0',
      },
      ...(escalationEnabled
        ? [
            {
              label: t
                ? t('protocols.firstEscalation', '1st Escalation')
                : '1st Escalation',
              value: '1',
            },
            {
              label: t
                ? t('protocols.secondEscalation', '2nd Escalation')
                : '2nd Escalation',
              value: '2',
            },
          ]
        : []),
    ],
  });
}

function dropdownToLevel(value: string): number | null {
  if (value === UNSUBSCRIBED) return null;
  const n = Number(value);
  return Number.isFinite(n) && LEVELS.includes(n as Level) ? n : null;
}

/** Dropdown value for one axis of a role: DEFAULT when the role has no
 *  type-override cell (inherits), else the explicit level (or Unsubscribed
 *  for an explicit-null axis). */
export function axisDropdownValue(
  cell: RubricCell | undefined,
  axis: 'alert' | 'drill',
  escalationEnabled: boolean,
): string {
  if (!cell) return DEFAULT;
  const level = axis === 'alert' ? cell.alert_min_level : cell.drill_min_level;
  if (level == null) return UNSUBSCRIBED;
  if (!LEVELS.includes(level as Level)) return UNSUBSCRIBED;
  // Clamp so a stored level-1/2 cell still selects an option the
  // collection contains (otherwise the closed dropdown falls back to the
  // "Unsubscribed" placeholder, which would misreport the row). The cell is
  // never rewritten — display only.
  return String(displayLevel(level, escalationEnabled));
}

/**
 * The value to re-send for the axis the user did NOT touch.
 *
 * Both axes ride in every write (changing one must not silently
 * unsubscribe the other), so this is the value the server sees for the
 * untouched axis, and the server's write gate compares it against what is
 * actually in force.
 *
 * When a cell exists it answers with the cell's OWN value, **NULL included**.
 * A NULL axis on a stored row is a real answer — "Unsubscribed for this
 * protocol" — and this grid is what writes it (picking Unsubscribed on one
 * axis PATCHes `{alert: null, …}`; `writeCell` only DELETEs when BOTH go
 * null). A `cell?.X ?? inherited.X` fallback would turn that NULL into the
 * district default's level, so editing the *other* axis would propose an
 * escalation level the site does not have — and with the flag off, level 2
 * is not even in the option list the admin is choosing from.
 *
 * `inherited` is the right fallback only where it is genuinely in force:
 * when the role has no cell at all and the row is about to be created.
 */
export function preservedAxis(
  cell: RubricCell | undefined,
  axis: 'alert' | 'drill',
  inherited: { alert: number | null; drill: number | null },
): number | null {
  if (cell) {
    const raw = axis === 'alert' ? cell.alert_min_level : cell.drill_min_level;
    return raw ?? null;
  }
  return axis === 'alert' ? inherited.alert : inherited.drill;
}

function findCellForRole(
  cells: RubricCell[],
  roleId: number,
  alertTypeId: number,
): RubricCell | undefined {
  // The Recipients tab only edits type-override cells, so the resolver
  // logic is intentionally narrow: same role + same alert_type, where
  // the cell-key default fields are NULL.
  return cells.find(
    (c) =>
      c.role === roleId &&
      c.alert_type === alertTypeId &&
      c.urgency === null &&
      c.mode === null,
  );
}

export function ProtocolRecipientsEditor({
  scope,
  alertTypeId,
  urgency,
  mode,
}: ProtocolRecipientsEditorProps) {
  const { t } = useTranslation();
  // ADR-117 Mechanism B gate, resolved for the scope this editor
  // writes to — the site for a school-tier scope, the district for a
  // district-tier one. NOT `useFeature`, which unions across every visible
  // site when no active organization is selected: an admin who also manages a
  // flag-ON district would be offered escalation options here that the backend
  // then 400s. Fails closed — no scope (create flow), an unloaded payload, or
  // a scope absent from it all read OFF.
  const escalationEnabled = useScopedFeature(
    FeatureCode.ALERT_ESCALATION,
    scope?.tier === 'district' ? 'organization' : 'site',
    scope?.tier === 'district' ? scope.districtId : (scope?.schoolId ?? null),
  );
  const { data: groupsData, isLoading: isLoadingGroups } = useGroups({
    page_size: 200,
    // Hide Internal Staff and Vendor Staff roles from recipient pickers,
    // even for callers with `accounts.view_all_roles` (ADR-100).
    exclude_hidden: true,
  });

  // Both scopes use the same filter shape; the underlying hooks differ
  // only by which `/api/v2/rubric/{tier}/{id}/cells/` endpoint they
  // hit. Call both hooks and pick the one matching the active tier —
  // React-Query disables the inactive one via `enabled: !!id`.
  const schoolScope = scope?.tier === 'school' ? scope : null;
  const districtScope = scope?.tier === 'district' ? scope : null;

  const { data: schoolCells = [], isLoading: schoolLoading } =
    useSchoolRubricCells(schoolScope?.schoolId ?? null, {
      alert_type: alertTypeId ?? undefined,
    });
  const { data: districtCells = [], isLoading: districtLoading } =
    useDistrictRubricCells(districtScope?.districtId ?? null, {
      alert_type: alertTypeId ?? undefined,
    });
  // When editing T2, also fetch the parent T1 cells
  // so we can render "inherited from district template" hints on
  // rows that have no T2 override.
  const inheritanceDistrictId =
    schoolScope?.districtId != null ? schoolScope.districtId : null;
  const { data: inheritanceCells = [] } = useDistrictRubricCells(
    inheritanceDistrictId,
    { alert_type: alertTypeId ?? undefined },
  );

  // Fetch the district `(urgency, mode)` cell-default — the value the
  // backend seeds at protocol creation — so a role with no type-override
  // renders "Default · <inherited>" instead of a misleading "Unsubscribed".
  // School scope inherits from the parent district; district scope from the
  // same district. `follow` urgency has no rubric default → skip. The API
  // filters on the stored `activation_mode` value directly, so we
  // pass it through; the cast bridges the loose `RubricMode`
  // ('alert' | 'report') type to the stored 'alert' | 'incident' values.
  const defaultsDistrictId =
    scope?.tier === 'district'
      ? (districtScope?.districtId ?? null)
      : inheritanceDistrictId;
  const canResolveDefault =
    (urgency === 'emergency' || urgency === 'attention') && mode != null;
  const { data: districtDefaultCells = [] } = useDistrictRubricCells(
    canResolveDefault ? defaultsDistrictId : null,
    canResolveDefault
      ? {
          urgency: urgency as RubricUrgency,
          mode: mode as unknown as RubricMode,
        }
      : {},
  );

  const createSchoolMut = useCreateSchoolRubricCell(schoolScope?.schoolId ?? 0);
  const patchSchoolMut = usePatchSchoolRubricCell(schoolScope?.schoolId ?? 0);
  const deleteSchoolMut = useDeleteSchoolRubricCell(schoolScope?.schoolId ?? 0);
  const createDistrictMut = useCreateDistrictRubricCell(
    districtScope?.districtId ?? 0,
  );
  const patchDistrictMut = usePatchDistrictRubricCell(
    districtScope?.districtId ?? 0,
  );
  const deleteDistrictMut = useDeleteDistrictRubricCell(
    districtScope?.districtId ?? 0,
  );

  // Select the active tier's view of the world. The inactive tier's
  // hooks return empty / no-op data, so reading from these is safe.
  const cells: RubricCell[] =
    scope?.tier === 'district' ? districtCells : schoolCells;
  const isLoadingCells =
    scope?.tier === 'district' ? districtLoading : schoolLoading;
  const createMut =
    scope?.tier === 'district' ? createDistrictMut : createSchoolMut;
  const patchMut =
    scope?.tier === 'district' ? patchDistrictMut : patchSchoolMut;
  const deleteMut =
    scope?.tier === 'district' ? deleteDistrictMut : deleteSchoolMut;

  // No cached pending-level state: `Unsubscribed` is one of the dropdown
  // values, so each row's level is read straight off the server cell —
  // `null` when the axis is unsubscribed.

  const visibleRoles = useMemo(() => {
    const all = groupsData?.results ?? [];
    // Render every available role; let the customer's admin decide
    // which to subscribe rather than hardcoding the 6-role customer
    // bucket here (see /tmp/sms-wireframe/index.html for the eventual
    // grouping).
    //
    // Pure alphabetical sort by display name — no `is_system_role`-first
    // precedence, since the row shows no "System role" cue. Locale-aware
    // comparison so accented characters slot in sensibly.
    return [...all].sort((a, b) =>
      (a.display_name || a.name).localeCompare(
        b.display_name || b.name,
        undefined,
        { sensitivity: 'base' },
      ),
    );
  }, [groupsData]);

  const writeCell = useCallback(
    async (
      roleId: number,
      next: { alert_min_level: number | null; drill_min_level: number | null },
    ) => {
      if (scope === null || alertTypeId === null) {
        toaster.create({
          type: 'info',
          title: t(
            'protocols.saveProtocolFirstTitle',
            'Save the protocol first',
          ),
          description: t(
            'protocols.saveProtocolFirstDescription',
            'Recipients can be configured after the protocol exists; site + name are required.',
          ),
        });
        return;
      }

      const existing = findCellForRole(cells, roleId, alertTypeId);

      const isClear =
        next.alert_min_level === null && next.drill_min_level === null;
      if (isClear) {
        if (!existing) return;
        await deleteMut.mutateAsync(existing.id);
        return;
      }

      const writeBody: RubricCellWritePayload = {
        role: roleId,
        alert_type: alertTypeId,
        urgency: null,
        mode: null,
        alert_min_level: next.alert_min_level,
        drill_min_level: next.drill_min_level,
      };

      if (existing) {
        await patchMut.mutateAsync({
          cellId: existing.id,
          body: {
            alert_min_level: next.alert_min_level,
            drill_min_level: next.drill_min_level,
          },
        });
        return;
      }

      await createMut.mutateAsync(writeBody);
    },
    [cells, alertTypeId, scope, createMut, patchMut, deleteMut, t],
  );

  // The role's inherited (alert, drill) levels when it has no
  // type-override cell — district type-override (school scope, tier 3) then
  // the district `(urgency, mode)` cell-default (tier 4). Mirrors the
  // resolver's fallback order; school-tier `(urgency, mode)` defaults are
  // practically unused (defaults live at the district tier) so they're
  // omitted here.
  const inheritedForRole = useCallback(
    (roleId: number): { alert: number | null; drill: number | null } => {
      const typeOverride =
        scope?.tier === 'school' && alertTypeId != null
          ? findCellForRole(inheritanceCells, roleId, alertTypeId)
          : undefined;
      if (typeOverride) {
        return {
          alert: typeOverride.alert_min_level,
          drill: typeOverride.drill_min_level,
        };
      }
      const def = districtDefaultCells.find(
        (c) => c.role === roleId && c.alert_type === null,
      );
      return {
        alert: def?.alert_min_level ?? null,
        drill: def?.drill_min_level ?? null,
      };
    },
    [scope, alertTypeId, inheritanceCells, districtDefaultCells],
  );

  const renderSubscriptionSelect = useCallback(
    (
      kind: 'alert' | 'drill',
      roleId: number,
      ariaLabel: string,
      value: string,
      collection: ReturnType<typeof subscriptionCollectionFor>,
      onChange: (v: string) => void,
    ) => (
      <Box w="120px">
        <Select.Root
          collection={collection}
          value={[value]}
          onValueChange={(details) => {
            const v = details.value[0];
            if (v) onChange(v);
          }}
          size="xs"
          data-e2e={`recipient-${kind}-${roleId}`}
        >
          <Select.HiddenSelect aria-label={ariaLabel} />
          <Select.Control>
            <Select.Trigger aria-label={ariaLabel}>
              <Select.ValueText
                placeholder={t('protocols.unsubscribed', 'Unsubscribed')}
              />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Portal>
            <Select.Positioner>
              <Select.Content>
                {collection.items.map((item) => (
                  <Select.Item item={item} key={item.value}>
                    <Select.ItemText>{item.label}</Select.ItemText>
                    <Select.ItemIndicator />
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Positioner>
          </Portal>
        </Select.Root>
      </Box>
    ),
    [t],
  );

  type RecipientRow = (typeof visibleRoles)[number];

  const columns: Column<RecipientRow>[] = useMemo(
    () => [
      {
        key: 'role',
        label: t('common.role', 'Role'),
        render: (role) => {
          // `alertTypeId` is guaranteed non-null by the guard below, which cannot
          // sit above this block: `columns` is a useMemo and an early return
          // before it would make hook order conditional. Narrow explicitly.
          const cell =
            alertTypeId == null
              ? undefined
              : findCellForRole(cells, role.id, alertTypeId);
          const inherited = !cell ? inheritedForRole(role.id) : null;
          const showInheritedHint =
            inherited != null &&
            (inherited.alert != null || inherited.drill != null);

          return (
            <Box>
              <Text fontSize="sm" color="text.primary">
                {role.display_name || role.name}
              </Text>
              {showInheritedHint && (
                <Text
                  fontSize="2xs"
                  color="text.tertiary"
                  fontStyle="italic"
                  data-e2e={`recipient-inherited-hint-${role.id}`}
                >
                  {t(
                    'protocols.inheritedFromDistrict',
                    'Inherited from district template',
                  )}
                </Text>
              )}
            </Box>
          );
        },
      },
      {
        key: 'alert',
        label: t('events.toggle.alerts', 'Alert'),
        width: '160px',
        render: (role) => {
          // `alertTypeId` is guaranteed non-null by the guard below, which cannot
          // sit above this block: `columns` is a useMemo and an early return
          // before it would make hook order conditional. Narrow explicitly.
          const cell =
            alertTypeId == null
              ? undefined
              : findCellForRole(cells, role.id, alertTypeId);
          const inherited = inheritedForRole(role.id);
          const alertValue = axisDropdownValue(
            cell,
            'alert',
            escalationEnabled,
          );
          const onAlertChange = (newValue: string) => {
            if (newValue === DEFAULT) {
              void writeCell(role.id, {
                alert_min_level: null,
                drill_min_level: null,
              });
              return;
            }
            void writeCell(role.id, {
              alert_min_level: dropdownToLevel(newValue),
              drill_min_level: preservedAxis(cell, 'drill', inherited),
            });
          };
          return renderSubscriptionSelect(
            'alert',
            role.id,
            t(
              'protocols.alertSubscriptionFor',
              'Alert subscription for {{name}}',
              { name: role.display_name || role.name },
            ),
            alertValue,
            subscriptionCollectionFor(inherited.alert, escalationEnabled, t),
            onAlertChange,
          );
        },
      },
      {
        key: 'drill',
        label: t('events.toggle.drills', 'Drill'),
        width: '160px',
        render: (role) => {
          // `alertTypeId` is guaranteed non-null by the guard below, which cannot
          // sit above this block: `columns` is a useMemo and an early return
          // before it would make hook order conditional. Narrow explicitly.
          const cell =
            alertTypeId == null
              ? undefined
              : findCellForRole(cells, role.id, alertTypeId);
          const inherited = inheritedForRole(role.id);
          const drillValue = axisDropdownValue(
            cell,
            'drill',
            escalationEnabled,
          );
          const onDrillChange = (newValue: string) => {
            if (newValue === DEFAULT) {
              void writeCell(role.id, {
                alert_min_level: null,
                drill_min_level: null,
              });
              return;
            }
            void writeCell(role.id, {
              alert_min_level: preservedAxis(cell, 'alert', inherited),
              drill_min_level: dropdownToLevel(newValue),
            });
          };
          return renderSubscriptionSelect(
            'drill',
            role.id,
            t(
              'protocols.drillSubscriptionFor',
              'Drill subscription for {{name}}',
              { name: role.display_name || role.name },
            ),
            drillValue,
            subscriptionCollectionFor(inherited.drill, escalationEnabled, t),
            onDrillChange,
          );
        },
      },
    ],
    [
      alertTypeId,
      cells,
      escalationEnabled,
      inheritedForRole,
      renderSubscriptionSelect,
      t,
      writeCell,
    ],
  );

  if (!scope || alertTypeId == null) {
    return (
      <Text fontSize="sm" color="text.secondary">
        {t(
          'protocols.saveProtocolFirst',
          'Save this protocol first before setting per-role recipient rules.',
        )}
      </Text>
    );
  }

  if (isLoadingGroups || isLoadingCells) {
    return (
      <Center py={6}>
        <Spinner size="sm" />
      </Center>
    );
  }

  if (visibleRoles.length === 0) {
    return (
      <Text fontSize="sm" color="text.secondary">
        {t(
          'protocols.noRolesAvailable',
          'No roles available — ask an admin to define at least one role before configuring recipients.',
        )}
      </Text>
    );
  }

  const isMutating =
    createMut.isPending || patchMut.isPending || deleteMut.isPending;

  return (
    <Box
      opacity={isMutating ? 0.7 : 1}
      pointerEvents={isMutating ? 'none' : 'auto'}
    >
      <Box maxH="360px" overflowY="auto">
        <DataTable<RecipientRow>
          columns={columns}
          data={visibleRoles}
          keyExtractor={(role) => String(role.id)}
          caption={t(
            'protocols.perRoleSubscriptions',
            'Per-role subscriptions for this protocol',
          )}
        />
      </Box>
    </Box>
  );
}
